import { useCallback } from 'react';
import type { CustomerSummaryDto } from '@bhops/shared';
import { customersApi } from '@/api/customers';
import { EntityPicker } from './EntityPicker';

// Customer search-as-you-type. Built on EntityPicker; defaults to
// returning up to 20 active customers per query. The caller owns
// the `value` (full DTO or null) so downstream forms can read the
// id + displayName without a second lookup.

interface CustomerPickerProps {
  value: CustomerSummaryDto | null;
  onChange: (customer: CustomerSummaryDto | null) => void;
  disabled?: boolean;
  placeholder?: string;
  'data-testid'?: string;
}

export function CustomerPicker({
  value,
  onChange,
  disabled,
  placeholder = '고객명 / 사업자명 검색…',
  'data-testid': testId,
}: CustomerPickerProps) {
  const fetchOptions = useCallback(async (query: string): Promise<CustomerSummaryDto[]> => {
    // pageSize 20 — small enough to render unvirtualized, large
    // enough to surface typical typo-correction candidates.
    const resp = await customersApi.list({ search: query, pageSize: 20 });
    return resp.items;
  }, []);

  return (
    <EntityPicker<CustomerSummaryDto>
      value={value}
      onChange={onChange}
      fetchOptions={fetchOptions}
      queryKey="customer-picker"
      getId={(c) => c.id}
      getLabel={(c) => c.displayName || c.legalName || c.id}
      getSubLabel={(c) =>
        [c.legalName, c.salesOwnerCode, c.countryCode]
          .filter((s): s is string => !!s && s !== c.displayName)
          .join(' · ')
      }
      disabled={disabled}
      placeholder={placeholder}
      data-testid={testId ?? 'customer-picker'}
      emptyMessage="일치하는 고객이 없습니다."
    />
  );
}
